import { useState, useSyncExternalStore } from 'react';
import { createPortal } from 'react-dom';
import { createRoot } from 'react-dom/client';
import { getElements, subscribe } from '../bridge/bridgeManager';
import {
  ChakraProvider,
  extendTheme,
  Box,
  ThemeConfig,
  ColorModeScript,
} from '@chakra-ui/react';
import { customTheme } from './resources/theme/theme';
import './i18n/config';
import { CustomFonts } from './resources/fonts';
import { HashRouter } from 'react-router-dom';
import { AutoUpdateEvents } from './components/shared/autoUpdate/AutoUpdateEvents';
import { SessionStatus } from './api/endpoints/sessionApi';
import { HttpStatus } from './api/httpClient/types';
import { ErrorDetails } from './components/shared/alert/ErrorDetails';
import {
  useSessionState,
  SessionStateProvider,
  useSession,
} from './hooks/useSession';
import { LoadingSessionView } from './LoadingSessionView';
import {
  TrackerContext,
  useTracker,
} from './components/shared/tracker/useTracker';
import { MessageBusProvider } from './messageBus/useMessageBus';
import { MessageBus } from './messageBus/messageBus';
import { ConfigureProductApproval } from './components/productApproval/ConfigureProductApproval';
import {
  JobRecordsStoreProvider,
  useJobRecordsStore,
} from './components/jobs/useJobRecordsStore';
import {
  UrlParamsProvider,
  useUrlParams,
} from './components/shared/hooks/useUrlParams';
import { MetadataProvider } from './hooks/metadata/useMetadata';
import { VersionMismatchBanner } from './components/shared/VersionMismatchBanner';
import { GlobalSearchModal } from './components/search/GlobalSearchModal';
import { EntityHistoryProvider } from './hooks/useEntityHistory';
import { ClientConfigurationProvider } from './hooks/useClientConfiguration';
import { ActiveSession } from './components/shared/ActiveSession';

const config: ThemeConfig = {
  initialColorMode: 'dark',
  useSystemColorMode: false,
};

const theme = extendTheme({ config }, customTheme);

function Root() {
  const session = useSessionState();
  const [messageBus] = useState(() => new MessageBus());

  const params = useUrlParams();

  return (
    <ClientConfigurationProvider>
      <MessageBusProvider value={messageBus}>
        <SessionStateProvider value={session}>
          <HashRouter>
            <UrlParamsProvider value={params}>
              <ChakraProvider theme={theme}>
                <EntityHistoryProvider>
                  <App />
                </EntityHistoryProvider>
              </ChakraProvider>
            </UrlParamsProvider>
          </HashRouter>
        </SessionStateProvider>
      </MessageBusProvider>
    </ClientConfigurationProvider>
  );
}

function App() {
  const tracker = useTracker();
  const jobsStore = useJobRecordsStore();
  return (
    <JobRecordsStoreProvider value={jobsStore}>
      <TrackerContext.Provider value={tracker}>
        <MetadataProvider>
          <VersionMismatchBanner />
          <AutoUpdateEvents>
            <ConfigureProductApproval>
              <ColorModeScript
                initialColorMode={theme.config.initialColorMode}
              />
              <CustomFonts />
              <ActiveSession>
                <GlobalSearchModal />
              </ActiveSession>
              <BridgeElements />
            </ConfigureProductApproval>
          </AutoUpdateEvents>
        </MetadataProvider>
      </TrackerContext.Provider>
    </JobRecordsStoreProvider>
  );
}

function BridgeElements() {
  const elements = useSyncExternalStore(subscribe, getElements);
  const { sessionState } = useSession();

  if (sessionState.status === SessionStatus.Inactive) {
    return null;
  }

  if (sessionState.status === SessionStatus.Fetching) {
    return <LoadingSessionView />;
  }

  if (
    sessionState.status === SessionStatus.Error &&
    sessionState.error.status !== HttpStatus.Unauthorized
  ) {
    return <ErrorDetails error={sessionState.error} />;
  }

  return (
    <>
      {elements.map((e) =>
        createPortal(
          <Box color="chakra-body-text" h="full">
            {e.element}
          </Box>,
          e.container,
        ),
      )}
    </>
  );
}

export function renderReactRoot(container: Element) {
  const root = createRoot(container);
  root.render(<Root />);
}
